// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { createContext, createElement, useContext, useSyncExternalStore, type ReactNode } from "react";

// the locale every number, date and currency in the library is formatted with. Intl called with an undefined
// locale uses the server's language on the server and the browser's in the browser, so the two renders disagree
// ("1,234.5" vs "1.234,5") and hydration fails. components resolve their locale through useLocale instead:
//   1. the component's own locale prop
//   2. the nearest <LocaleProvider locale="en-GB"> (put one in the root layout, e.g. ro-RO for a Romanian app)
//   3. after hydration: <html lang>, then the browser's language
//   4. on the server and during hydration: DEFAULT_LOCALE
export const DEFAULT_LOCALE = "en-US";

const LocaleContext = createContext<string | undefined>(undefined);

export type LocaleProviderProps = { locale: string; children?: ReactNode };

// sets the locale for everything inside it, on the server too, so there is no swap after hydration
export function LocaleProvider({ locale, children }: LocaleProviderProps) {
  return createElement(LocaleContext.Provider, { value: locale }, children);
}

function subscribe(onChange: () => void) {
  window.addEventListener("languagechange", onChange);
  return () => window.removeEventListener("languagechange", onChange);
}

function valid(locale: string | undefined | null) {
  if (!locale) return undefined;
  try {
    return Intl.getCanonicalLocales(locale)[0];
  } catch {
    return undefined;
  }
}

// the page's language, then the browser's; DEFAULT_LOCALE when neither is usable. safe outside react (handlers)
export function getBrowserLocale(): string {
  if (typeof document === "undefined") return DEFAULT_LOCALE;
  return valid(document.documentElement.lang) ?? valid(navigator.language) ?? DEFAULT_LOCALE;
}

// the locale to format with: useLocale(props.locale)
export function useLocale(locale?: string): string {
  const provided = useContext(LocaleContext);
  const browser = useSyncExternalStore(subscribe, getBrowserLocale, () => DEFAULT_LOCALE);
  return valid(locale) ?? valid(provided) ?? (provided === undefined ? browser : DEFAULT_LOCALE);
}
